import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withRouter } from 'next/router';
import { encode, decode } from 'querystring';
import omit from 'lodash/omit';
import tracking from '@g4/g4-analytics';



import ERROR_BOUNDARY_TRANSLATIONS_QUERY from './errorBoundary/errorBoundaryTranslations.query.graphql.js';

import {
  withTranslations,
  TranslationsContext,
} from '../graphql/components/TranslationsProvider';

import {
  Button,
  ThemeProvider,
  VerticalSpacing,
  Popup,
  theme,
  Box,
} from './Boneyard';

import logError from './errorBoundary/logError.js';
import * as errorConstructors from './errorBoundary/errors.js';
import { makeStep, breadcrumbSteps } from '../hooks/useBookingSteps.js';
import { ErrorContext } from '../hooks/useErrorHandling';
import { RECOVERABLE_ERROR_BOUNDARY } from '../../automationHooks.json';
import Markdown from './Markdown.js';
import { peacockBlue, waterBlue } from '@g4/boneyard/lib/theme/colors';
import usePopupError from '../hooks/usePopupError.js';

export const makeError = ({ error, errorMessages }) => {
  const Error = errorConstructors[error];

  if (!Error) {
    return null;
  }
  errorMessages =
    typeof errorMessages === 'string' ? [errorMessages] : errorMessages;

  return new Error(errorMessages);
};
const encodeError = error =>
  encode({
    error: error.name,
    errorMessages: error.graphqlErrors,
  });

const capitalizeFirstLetter = string => {
  if (!string) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Title = styled.h1`
  font-size: 26px;
  text-align: center;
  color: ${waterBlue};
  margin-bottom: 14px;
  font-weight: 600;
  ${props => props.theme.query.md} {
    padding: 0 35px;
  }
`;

const Content = styled.div`
  text-align: center;
  color: ${peacockBlue};
  line-height: 1.6;
  font-size: 14px;

  p:not(:last-child) {
    margin-bottom: 7px;
  }

  ${props => props.theme.query.md} {
    padding: 0 45px;
  }
`;
const StyledButton = styled(Button)`
  width: 100%;
  ${props => props.theme.query.md} {
    max-width: 200px;
  }
`;

class ErrorBoundary extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    router: PropTypes.shape({
      query: PropTypes.shape({
        transactionId: PropTypes.string,
        error: PropTypes.string,
        isStadium: PropTypes.bool,
      }).isRequired,
      asPath: PropTypes.string,
      replace: PropTypes.func.isRequired,
      push: PropTypes.func.isRequired,
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    setError: PropTypes.func,
  };

  static contextType = TranslationsContext;

  static getDerivedStateFromError(error) {
    // TODO: think about recoverable errors which aren't to graphql
    // e.g. client only errors
    const {
      routeMap = null,
      graphqlErrors = null,
      networkError = null,
    } = error;
    const canHandleAllErrors = graphqlErrors?.every(err => routeMap?.[err]);

    if (!routeMap || !canHandleAllErrors) {
      return {
        hasUnrecoverableError: true,
        recoverableError: null,
        isPopupActive: networkError?.statusCode !== 422,
      };
    }

    // TODO: Consider if some errors are more important, perhaps sort them by the index in the step
    return {
      hasUnrecoverableError: false,
      recoverableError: error,
      isPopupActive: networkError?.statusCode !== 422,
    };
  }

  state = {
    hasUnrecoverableError: false,
    recoverableError: null,
    isPopupActive: false,
  };

  componentDidMount() {
    const errorFromUrl = makeError(this.props.router.query);

    if (errorFromUrl) {
      // we're already in the right place, since the url contains the error
      this.setState({
        recoverableError: errorFromUrl,
        isPopupActive: false,
      });
    }
  }

  componentDidCatch(error) {
    // Don't send errors to logging service in test env
    if (process.env.NODE_ENV === 'test') {
      console.error(error);
    } else {
      this.trackError(error);
      logError(error);
    }
  }

  getHandlingStep(recoverableError) {
    if (!recoverableError) {
      return [];
    }

    const { routeMap, graphqlErrors = [] } = recoverableError;

    const isStadium = this.props?.router?.query?.isStadium;

    const getStep = stepName =>
      breadcrumbSteps(isStadium).find(({ key }) => key === stepName);
    // TODO: what about non-graphql errors
    const nextError = graphqlErrors[0];

    if (!routeMap || !nextError) {
      return [];
    }
    const step = makeStep(
      getStep(routeMap[nextError]),
      this.props.router.query.transactionId
    );

    return [
      isStadium ? `${step.as}` : `${step.as}?${encodeError(recoverableError)}`,
      step,
    ];
  }

  trackError(recoverableError) {
    tracking.trackEvent(
      'client_side_error',
      `Middleware Error | ${recoverableError?.graphqlErrors?.[0]}`
    );
  }

  getTranslationForError(recoverableError) {
    const {
      errorBoundaryTitle,
      errorBoundaryMessage,
      errorBoundaryContinueButtonLabel,
      ...rest
    } = this.context.errors;

    if (!recoverableError) {
      return {
        title: errorBoundaryTitle,
        message: errorBoundaryMessage,
        continueButtonLabel: errorBoundaryContinueButtonLabel,
      };
    }

    // Special handling for VEHICLE_UNAVAILABLE - show UNKNOWN_ERROR messages
    const errorType = recoverableError.graphqlErrors[0] === 'VEHICLE_UNAVAILABLE'
      ? 'UNKNOWN_ERROR'
      : recoverableError.graphqlErrors[0];

    const key = [recoverableError.name, errorType].join('_');

    return {
      title: rest[key + '_title'] ? rest[key + '_title'] : errorBoundaryTitle,
      message: rest[key + '_message']
        ? rest[key + '_message']
        : errorBoundaryMessage,
      continueButtonLabel: rest[key + '_continueButtonLabel']
        ? rest[key + '_continueButtonLabel']
        : errorBoundaryContinueButtonLabel,
    };
  }



  navigateAndClosePopup = () => {
    if (this.state.hasUnrecoverableError) {
      window.location = '/booking';

      return;
    }

    const [url, handlingStep] = this.getHandlingStep(
      this.state.recoverableError
    );

    const { graphqlErrors = [] } = this.state.recoverableError || {};

    const nextError = graphqlErrors[0];



    if (url && handlingStep) {
      let { setError } = this.props;
      // saving the error in local storage so that the page we redirect to is aware of the error
      // incase we wamt to perform data related cleaning chores
      nextError && setError(nextError);
      // if we already have an error replace the state so we don't keep the error in the history
      if (this.props.router.query.error) {
        this.props.router.replace(handlingStep.href, url);
      } else {
        this.props.router.push(handlingStep.href, url);
      }
      // 'shouldShowPopup' state variable wasn't being used
      this.setState({
        isPopupActive: false,
      });
    }
  };

  clearErrorMessages = (...errorMessagesToClear) => {
    const { recoverableError } = this.state;

    if (!recoverableError) {
      return;
    }

    const errorMessages = recoverableError.graphqlErrors.filter(
      errorCode => !errorMessagesToClear.includes(errorCode)
    );
    const errorName = recoverableError.name;

    if (errorMessages?.length) {
      this.setState({
        recoverableError: makeError({
          error: errorName,
          errorMessages,
        }),
        isPopupActive: true,
      });
    } else {
      this.setState({
        recoverableError: null,
        isPopupActive: false,
      });
    }

    const [pathname, queryString] = this.props.router.asPath.split('?');
    const urlQueryParams = decode(queryString);

    // Don't save the error in the history
    this.props.router.replace(this.props.router.pathname, {
      pathname,
      query: omit(urlQueryParams, 'error', 'errorMessages'),
    });
  };

  getMappedTitleMessage() {
    const isStadium = this.props?.router?.query?.isStadium;
    let popupTitle, popupMessage;

    const errorCodeMapping = {
      SHOW_SEATS_UNAVAILABLE: {
        title: 'allegiantStadiumSeatsUnavailableTitle',
        message: 'allegiantStadiumSeatsUnavailableMessage',
      },
      SHOW_UNAVAILABLE: {
        title: 'allegiantStadiumSeatsUnavailableTitle',
        message: 'allegiantStadiumSeatsUnavailableMessage',
      },
    };

    const graphQLError = this?.state?.recoverableError?.graphqlErrors?.[0];

    if (isStadium && Object.keys(errorCodeMapping).includes(graphQLError)) {
      popupTitle = this.context.errors[errorCodeMapping[graphQLError].title];
      popupMessage = this.context.errors[
        errorCodeMapping[graphQLError].message
      ];
    }

    return { popupTitle, popupMessage };
  }

  renderErrorPopup() {
    const { recoverableError, hasUnrecoverableError } = this.state;

    if (!recoverableError && !hasUnrecoverableError) {
      return null;
    }

    const { title, message, continueButtonLabel } = this.getTranslationForError(
      recoverableError
    );

    const { popupTitle, popupMessage } = this.getMappedTitleMessage();

    const step = this.props.router.pathname.split('/')[3];

    const capitalizedStep = capitalizeFirstLetter(step);

    return (
      <ThemeProvider theme={theme}>
        <Popup
          open={this.state.isPopupActive}
          title=" " //this is needed because of the design layout
          automationHook={RECOVERABLE_ERROR_BOUNDARY.hook}
          onRequestClose={this.navigateAndClosePopup}
        >
          <Box maxWidth="500px" d="flex" direction="column" alignItems="center">
            <Title>
              <Markdown
                source={popupTitle || title}
                data={{
                  ellipsis: '...',
                }}
              />
            </Title>
            {/* TODO should we loop over the errors? */}
            <Content>
              <Markdown source={popupMessage || message} />
            </Content>
            <VerticalSpacing size={4} />
            <StyledButton
              onClick={() => {
                tracking.trackEvent(
                  'Site Click Tracking',
                  capitalizedStep,
                  `Middleware Error | ${recoverableError?.graphqlErrors?.[0]}`,
                  null
                );
                this.navigateAndClosePopup();
              }}
            >
              {continueButtonLabel}
            </StyledButton>
          </Box>
        </Popup>
      </ThemeProvider>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderErrorPopup()}
        <ErrorContext.Provider
          value={{
            error: this.state.recoverableError,
            clearErrorMessages: this.clearErrorMessages,
            isPopupActive: this.state.isPopupActive,
          }}
        >
          <>{!this.state.hasUnrecoverableError && this.props.children}</>
        </ErrorContext.Provider>
      </React.Fragment>
    );
  }
}

const withHookHOC = Component => {
  const ComponentWithPropsFromHook = props => {
    const { setError } = usePopupError();
    return <Component {...props} setError={setError} />;
  };
  return ComponentWithPropsFromHook;
};

export default withRouter(
  withTranslations(
    ERROR_BOUNDARY_TRANSLATIONS_QUERY,
    withHookHOC(ErrorBoundary)
  )
);
